import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import BlockIcon from "@mui/icons-material/Block";
import { ReprintAuthRow } from "../../types/reprintAuth.type";
import { COPY_TYPES } from "../Customer/Details/printCopyTypes";
import { usePdfDownload } from "../../utils/hooks/usePdfDownload";
import { formatDate } from "../../utils/date";
import {
  CHARCOAL,
  STATUS_CHIP,
  TRANSACTION_TYPE_LABEL,
  getRemainingText,
  roleLabel,
} from "./data";

interface GrantDetailModalProps {
  open: boolean;
  grant: ReprintAuthRow | null;
  onClose: () => void;
  onOpenPrintHistory: (grant: ReprintAuthRow) => void;
  onRevoke: (grant: ReprintAuthRow) => void;
}

// Short ต้นฉบับ/สำเนา tag per copy type, keyed for compact chip rendering.
const COPY_SHORT = COPY_TYPES.reduce<Record<string, string>>((acc, t) => {
  acc[t.key] = t.short;
  return acc;
}, {});

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

const Field = ({ label, children }: FieldProps) => (
  <Box display="flex" justifyContent="space-between" gap={2} py={0.75}>
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: "right" }}>{children}</Box>
  </Box>
);

export default function GrantDetailModal({
  open,
  grant,
  onClose,
  onOpenPrintHistory,
  onRevoke,
}: GrantDetailModalProps) {
  const { downloadingId, downloadPdf, viewingId, openPdf } = usePdfDownload();

  if (!grant) return null;

  const chip = STATUS_CHIP[grant.status];
  const isActive = grant.status === "ACTIVE";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700, color: CHARCOAL, pr: 6 }}>
        สิทธิ์พิมพ์ซ้ำของบิลนี้
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box mb={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {grant.transaction_no}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {TRANSACTION_TYPE_LABEL[grant.transaction_type]} ·{" "}
            {grant.customer_name} ({grant.customer_code})
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />

        <Field label="สถานะ">
          <Chip size="small" label={chip.label} color={chip.color} />
        </Field>
        <Field label="สายส่ง">
          <Typography variant="body2">{grant.route_code || "-"}</Typography>
        </Field>
        <Field label="สร้างโดย">
          <Box>
            <Typography variant="body2">{grant.created_by_name || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {roleLabel(grant.created_by_role)}
            </Typography>
          </Box>
        </Field>
        <Field label="สร้างเมื่อ">
          <Typography variant="body2">
            {grant.created_at
              ? formatDate(grant.created_at, "DD/MM/YYYY HH:mm")
              : "-"}
          </Typography>
        </Field>
        <Field label="หมดอายุ">
          <Box>
            <Typography variant="body2">
              {grant.valid_until
                ? formatDate(grant.valid_until, "DD/MM/YYYY HH:mm")
                : "-"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getRemainingText(grant)}
            </Typography>
          </Box>
        </Field>
        <Field label="อนุญาตให้พิมพ์ซ้ำ">
          {grant.allowed_copies && grant.allowed_copies.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                justifyContent: "flex-end",
              }}
            >
              {grant.allowed_copies.map((copy) => (
                <Chip
                  key={copy.type}
                  size="small"
                  label={`${copy.type} ${COPY_SHORT[copy.type] ?? ""} ×${copy.quantity}`}
                  sx={{
                    fontWeight: 700,
                    bgcolor: "#5C69E812",
                    color: "#5C69E8",
                  }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2">ทั้งหมด</Typography>
          )}
        </Field>
        <Field label="เหตุผล">
          <Typography variant="body2">{grant.reason || "-"}</Typography>
        </Field>
        <Field label="พิมพ์ไปแล้ว">
          <Typography variant="body2">{grant.reprint_count} ครั้ง</Typography>
        </Field>

        <Divider sx={{ my: 1 }} />
        <Box display="flex" flexDirection="column" gap={1} mt={1}>
          <Button
            startIcon={<HistoryIcon />}
            variant="outlined"
            fullWidth
            onClick={() => onOpenPrintHistory(grant)}
          >
            ดูประวัติการพิมพ์
          </Button>
          <Box display="flex" gap={1}>
            <Button
              startIcon={
                viewingId === grant.transaction_uuid ? (
                  <CircularProgress size={16} />
                ) : (
                  <ReceiptLongIcon />
                )
              }
              variant="outlined"
              fullWidth
              disabled={viewingId !== null}
              onClick={() => openPdf(grant.transaction_uuid)}
            >
              ดูบิลขาย
            </Button>
            <Button
              startIcon={
                downloadingId === grant.transaction_uuid ? (
                  <CircularProgress size={16} />
                ) : (
                  <DownloadIcon />
                )
              }
              variant="outlined"
              fullWidth
              disabled={downloadingId !== null}
              onClick={() => downloadPdf(grant.transaction_uuid)}
            >
              ดาวน์โหลด PDF
            </Button>
          </Box>
        </Box>
      </DialogContent>
      {isActive && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            startIcon={<BlockIcon />}
            color="error"
            variant="contained"
            fullWidth
            onClick={() => onRevoke(grant)}
          >
            ยกเลิกสิทธิ์
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
